import httpClient from "@plugins/http";
import { isEmpty } from "@utils/var.check";

import {
  attemptRequestTransformer,
  getAttemptContext,
} from "@modules/attempt/service/attempt.api.utils";
import {
  AttemptableType,
  AttemptType,
  ResourceAttemptType,
} from "@modules/attempt/service/attempt.constant.ts";
import { AssignmentSettings, AttemptFeedbackStatus } from "@modules/attempt/types.ts";
import { ApiResponse } from "@apis/api.ts";
import { PackageProgressCompletionModel } from "@modules/account/types.ts";
import { LessonActivityBasicInfo } from "@/types/lesson";

const fetchQuizAttempt = (requestData) => {
  return httpClient.post(`/apis/attempt/quiz/get`, requestData, {
    transformRequest: [attemptRequestTransformer, ...httpClient.defaults.transformRequest],
  });
};
const fetchQuizQuestionAttempt = (uuid, requestData) => {
  return httpClient.post(`/apis/attempt/quiz/get/questions/${uuid}`, requestData, {
    transformRequest: [attemptRequestTransformer, ...httpClient.defaults.transformRequest],
  });
};
const fetchQuestionAttempt = (requestData) => {
  requestData = attemptRequestTransformer(requestData);

  return httpClient.get(`/apis/attempt/get/question`, { params: requestData });
};

export interface AttemptContext {
  attemptType: AttemptType;
  attemptableType: AttemptableType;
  attemptableUuid: string;
  fromCcpm: boolean;
}
export interface FetchQuizAttemptRawApiParams extends AttemptContext {
  activityId: number;
  userId: number;
  resourceAttemptType: ResourceAttemptType;
  refetch: boolean;
}
export interface FetchQuizQuestionAttemptRawApiParams extends AttemptContext {
  userId: number;
  resourceAttemptType: ResourceAttemptType;
  selectedAttemptIndex: number;
}
export interface FetchQuestionAttemptRawApiParams extends AttemptContext {
  componentId: string;
  questionuuid: string;
  userId: number;
  resourceAttemptType: ResourceAttemptType;
}

export interface FetchPollAttemptRawApiParams extends AttemptContext {
  pollUuid: string;
  userId: number;
  resourceAttemptType: ResourceAttemptType;
}

export interface PollAttempt {
  attemptableUuid: string | null;
  attemptableType: string | null;
  componentId: number | null;
  attemptType: string | null;
  pageCompleted: boolean;
  resourceAttemptType: string | null;
  lessonPageId: number | null;
  fromCcpm: boolean;
  registrationUuid: string | null;
  algebraKitSessionId: string | null;
  studentAttemptId: number;
  id: number;
  uuid: string | null;
  pollId: number;
  pollUuid: string;
  submittedBy: number;
  userId: number;
  answer: string;
  submittedByName: string | null;
  createdDate: string | null;
}
const fetchQuizAttemptRaw = async (requestData: FetchQuizAttemptRawApiParams) => {
  return httpClient.post(`/apis/attempt/quiz/get`, requestData);
};

const fetchQuizQuestionAttemptRaw = async (
  uuid: string,
  requestData: FetchQuizQuestionAttemptRawApiParams,
) => {
  return httpClient.post(`/apis/attempt/quiz/get/questions/${uuid}`, requestData);
};

const fetchQuestionAttemptRaw = async (requestData: FetchQuestionAttemptRawApiParams) => {
  return httpClient.get(`/apis/attempt/get/question`, { params: requestData });
};

const fetchQuestionsAttemptRaw = async (requestData: FetchQuestionAttemptRawApiParams[]) => {
  return httpClient.post(`/apis/attempt/get/questions`, requestData);
};

const fetchAggregatedPollAttempt = (requestData) => {
  const transformedRequestData = attemptRequestTransformer(requestData);
  return httpClient.get(`/apis/attempt/get/poll/aggregated-result`, {
    params: transformedRequestData,
  });
};

const fetchPollAttemptList = (requestData) => {
  return httpClient.post(`/apis/attempt/get/poll/result`, requestData, {
    transformRequest: [attemptRequestTransformer, ...httpClient.defaults.transformRequest],
  });
};

const fetchPollAttemptRaw = async (requestData: FetchPollAttemptRawApiParams) => {
  return httpClient.post(`/apis/attempt/get/poll/user-result`, requestData);
};

const fetchPollsAttemptRaw = async (requestData: FetchPollAttemptRawApiParams[]) => {
  return httpClient.post(`/apis/attempt/get/poll/user-results`, requestData);
};

const fetchUserPollAttempt = (requestData) => {
  return httpClient.post(`/apis/attempt/get/poll/user-result`, requestData, {
    transformRequest: [attemptRequestTransformer, ...httpClient.defaults.transformRequest],
  });
};

const fetchPackageCompletionInfo = (requestData) => {
  const transformedRequestData = attemptRequestTransformer(requestData);
  return httpClient.get(`/apis/attempt/progress/info?timestamp=${requestData.time}`, {
    params: transformedRequestData,
  });
};

const fetchCourseSectionProgress = (requestData) => {
  const transformedRequestData = attemptRequestTransformer(requestData);
  return httpClient.get(`/apis/attempt/progress/section-info`, {
    params: transformedRequestData,
  });
};

const fetchSectionPageProgress = (requestData) => {
  const transformedRequestData = attemptRequestTransformer(requestData);
  return httpClient.get(`/apis/attempt/progress/section/activity-info`, {
    params: transformedRequestData,
  });
};

const fetchSectionQuizActivityInfo = (requestData) => {
  const transformedRequestData = attemptRequestTransformer(requestData);
  return httpClient.get(`/apis/attempt/progress/section/quiz-info`, {
    params: transformedRequestData,
  });
};

const fetchPageProgress = (requestData) => {
  return httpClient.post(`/apis/attempt/progress/page-info`, requestData, {
    transformRequest: [attemptRequestTransformer, ...httpClient.defaults.transformRequest],
  });
};

const markAsComplete = (requestData): Promise<ApiResponse<PackageProgressCompletionModel[]>> => {
  const transformedRequestData = attemptRequestTransformer(requestData);
  const params = new URLSearchParams();
  params.append("userIds", [transformedRequestData.userId]);
  params.append("notifyStudent", transformedRequestData.notifyStudent);
  params.append("notificationMsg", transformedRequestData.notificationMsg || "");

  return httpClient.post(
    `/apis/attempt/progress/complete/${transformedRequestData.resourceAttemptType}/${transformedRequestData.attemptableUuid}`,
    params,
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    },
  );
};

const markSectionAsComplete = (requestData) => {
  const transformedRequestData = attemptRequestTransformer(requestData);
  const params = new URLSearchParams();
  params.append("studentUserIds", transformedRequestData.userIds);
  params.append("notifyStudent", transformedRequestData.notifyStudent);
  params.append("notificationMsg", transformedRequestData.notificationMsg || "");
  params.append("resourceAttemptType", transformedRequestData.resourceAttemptType || "");
  return httpClient.post(
    `/apis/attempt/section/progress/complete/${transformedRequestData.sectionId}/${transformedRequestData.attemptableUuid}`,
    params,
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    },
  );
};

const markSectionAsIncomplete = (requestData) => {
  const transformedRequestData = attemptRequestTransformer(requestData);
  const params = new URLSearchParams();
  params.append("studentUserIds", transformedRequestData.userIds);
  params.append("notifyStudent", transformedRequestData.notifyStudent);
  params.append("notificationMsg", transformedRequestData.notificationMsg || "");
  params.append("resourceAttemptType", transformedRequestData.resourceAttemptType || "");
  return httpClient.post(
    `/apis/attempt/section/progress/incomplete/${requestData.sectionId}/${requestData.attemptableUuid}`,
    params,
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    },
  );
};

const submitPollAttempt = (requestData) => {
  return httpClient.post(`/apis/attempt/poll/submitattempt`, requestData, {
    transformRequest: [attemptRequestTransformer, ...httpClient.defaults.transformRequest],
  });
};

const createPendingQuestionAttempt = (requestData) => {
  return httpClient.post(
    `/apis/attempt/creatependingattempt?questionUuid=${requestData.questionUuid}`,
    requestData,
    {
      transformRequest: [attemptRequestTransformer, ...httpClient.defaults.transformRequest],
    },
  );
};

const restartQuizAttempt = (requestData) => {
  return httpClient.post(`/apis/attempt/quiz/restart`, requestData, {
    transformRequest: [attemptRequestTransformer, ...httpClient.defaults.transformRequest],
  });
};

const startQuizAttempt = (requestData) => {
  return httpClient.post(`/apis/attempt/quiz/start`, requestData, {
    transformRequest: [attemptRequestTransformer, ...httpClient.defaults.transformRequest],
  });
};

const getQuizAttemptMarkStatus = (requestData) => {
  const transformedRequestData = attemptRequestTransformer(requestData);
  return httpClient.get(`/apis/attempt/quiz/get/mark-status`, {
    params: transformedRequestData,
  });
};
const submitQuizAttempt = (requestData) => {
  return httpClient.post(`/apis/attempt/quiz/submit`, requestData, {
    transformRequest: [attemptRequestTransformer, ...httpClient.defaults.transformRequest],
  });
};
const markAsCompleteQuizAttempt = (requestData) => {
  return httpClient.post(`/apis/attempt/quiz/mark-as-complete`, requestData, {
    transformRequest: [attemptRequestTransformer, ...httpClient.defaults.transformRequest],
  });
};
const submitQuizForStudent = (requestData) => {
  const params = new URLSearchParams();
  Object.keys(requestData).forEach((key) => {
    params.append(key, requestData[key]);
  });
  return httpClient.post(`/apis/attempt/submitempty`, params, {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });
};

const massSubmitForStudent = (requestData) => {
  const params = new URLSearchParams();
  Object.keys(requestData).forEach((key) => {
    params.append(key, requestData[key]);
  });
  return httpClient.post("/apis/attempt/masssubmit", params, {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });
};
const checkMassSubmitHasEntry = (requestData) => {
  return httpClient.get("/apis/attempt/masssubmit/has-entry", {
    params: requestData,
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });
};

const retryQuizAttempt = (requestData) => {
  return httpClient.post(`/apis/attempt/quiz/retry`, requestData, {
    transformRequest: [attemptRequestTransformer, ...httpClient.defaults.transformRequest],
  });
};

const submitQuestionAttempt = (requestData) => {
  return httpClient.post(`/apis/attempt/submitattempt`, requestData, {
    headers: {
      "X-With-Richtext": true,
    },
    transformRequest: [attemptRequestTransformer, ...httpClient.defaults.transformRequest],
  });
};

const saveQuestionAttempt = (requestData) => {
  return httpClient.post(`/apis/attempt/saveattempt`, requestData, {
    headers: {
      "X-With-Richtext": true,
    },
    transformRequest: [attemptRequestTransformer, ...httpClient.defaults.transformRequest],
  });
};

const fetchActivityAccess = (resourceType, resourceUuid, params) => {
  return httpClient.get(`/apis/attempt/activity/access/${resourceType}/${resourceUuid}`, {
    params,
  });
};

const fetchActivityAccessImmediately = (resourceType, resourceUuid, params) => {
  return httpClient.get(
    `/apis/attempt/activity/access/immediately/${resourceType}/${resourceUuid}`,
    {
      params,
    },
  );
};

const checkAssignmentAccess = async (uuid, userId): Promise<ApiResponse<void>> => {
  return await httpClient.get(`/apis/assignment/access/${uuid}`, {
    params: {
      userId: userId,
    },
  });
};

/**
 * Aggregated student response apis
 */
const fetchQuestionForAggregatedResponse = (params) => {
  return httpClient.get(`/apis/attempt/get-question`, {
    params: params,
  });
};

// TODO: [API] rename api url to attempt/aggregated-attempts
const fetchAggregatedStudentAttempts = (params) => {
  return httpClient.get(`/apis/question/aggregated-attempts`, {
    params: params,
  });
};

const saveViewCount = (params): Promise<ApiResponse> => {
  return httpClient.post(`/apis/resource-view/save/${params.type}/${params.uuid}`);
};

const updateLastAccessedDate = () => {
  const attemptContext = getAttemptContext();
  return httpClient.post(
    `/apis/attempt/access/update/lastaccess/${attemptContext.attemptableType}/${attemptContext.attemptableUuid}`,
  );
};

const fetchMarkStatus = (params) => {
  return httpClient.get(
    `/apis/attempt/mark-status/${params.questionAttemptUuid}/${params.academicYear}`,
  );
};

const fetchQuestionLFAStatus = async (
  questionAttemptUuid,
): Promise<ApiResponse<AttemptFeedbackStatus>> => {
  return await httpClient.get(`/apis/questionattempt/lfa-status/${questionAttemptUuid}`);
};

const fetchQuestionMultipleLFAStatus = (data) => {
  const params = { ...data };
  const questionAttemptUuid = params.questionAttemptUuid;
  delete params.questionAttemptUuid;
  return httpClient.get(`/apis/questionattempt/multiple-lfa-status/${questionAttemptUuid}`, {
    params: params,
  });
};

const reevaluateQuestionLFAFeedback = async (questionAttemptUuid): Promise<ApiResponse<string>> => {
  const transformedRequestData = attemptRequestTransformer({});
  const params = new URLSearchParams();
  params.append("resourceAttemptType", transformedRequestData.resourceAttemptType);
  params.append("attemptableType", transformedRequestData.attemptableType);
  params.append("attemptType", transformedRequestData.attemptType);

  return await httpClient.post(
    `/apis/questionattempt/lfa-reevaluate/${questionAttemptUuid}`,
    params,
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    },
  );
};

const fetchLessonSettings = async (
  lessonUuid,
  assignmentUuid,
): Promise<ApiResponse<AssignmentSettings>> => {
  const params = !isEmpty(assignmentUuid) ? { params: { assignmentUuid } } : {};
  return await httpClient.get(`/apis/lesson/settings/${lessonUuid}`, params);
};

export const createPendingQuestionAttemptForPrintView = (requestData): Promise<ApiResponse> => {
  return httpClient.post(
    `/apis/attempt/creatependingattempt?questionUuid=${requestData.questionUuid}`,
    requestData,
    {
      transformRequest: [
        (requestData) => {
          return requestData;
        },
        ...httpClient.defaults.transformRequest,
      ],
    },
  );
};

export const saveQuestionAttemptForPrintView = (requestData): Promise<ApiResponse> => {
  return httpClient.post(`/apis/attempt/saveattempt`, requestData, {
    transformRequest: [
      (requestData) => {
        return requestData;
      },
      ...httpClient.defaults.transformRequest,
    ],
  });
};

const startAttempt = (params) => {
  return httpClient.post(`/apis/attempt/start`, params);
};

const saveNumOfHintsUsed = (params) => {
  return httpClient.post(`/apis/attempt/update/hint-used`, params, {
    transformRequest: [attemptRequestTransformer, ...httpClient.defaults.transformRequest],
  });
};

const startActivity = (params) => {
  return httpClient.post(`/apis/attempt/activity/start`, params);
};

const fetchLessonActivities = async (params): Promise<ApiResponse<LessonActivityBasicInfo[]>> => {
  return await httpClient.get(`/apis/lesson/activity/info/${params.uuid}`);
};

const checkIsValidActivity = async (params): Promise<ApiResponse<boolean>> => {
  return await httpClient.get(`/apis/lesson/activity/is-valid/${params.id}`);
};

const fetchDiscussionAttempt = (requestData) => {
  requestData = attemptRequestTransformer(requestData);

  return httpClient.get(`/apis/attempt/get/discussion`, { params: requestData });
};

const fetchQuizLFAStatus = (params) => {
  const data = {};
  const transformedRequestData = attemptRequestTransformer({});
  data.academicYear = params.academicYear;
  data.attemptType = transformedRequestData.attemptType;
  if (params.teamId !== null) {
    data.teamId = params.teamId;
  }
  return httpClient.get(`/apis/attempt/quiz/lfa-status/${params.attemptHistoryUuid}`, {
    params: data,
  });
};

const fetchStudentQuizAttempts = (params) => {
  return httpClient.get(`/apis/quiz/aggregated-attempts`, {
    params: params,
  });
};

const checkAnswer = (requestData) => {
  return httpClient.post(`/apis/questionattempt/check-answer`, requestData, {
    headers: {
      "X-With-Richtext": true,
    },
    transformRequest: [attemptRequestTransformer, ...httpClient.defaults.transformRequest],
  });
};

const fetchLFADrafts = (questionAttemptId) => {
  return httpClient.get(`/apis/questionattempt/draft/get/${questionAttemptId}`);
};

const fetchHasAttempts = (lessonUuid) => {
  return httpClient.get(`/apis/attempt/user-attempt/exists/${lessonUuid}`);
};

const deletePreviewAttempts = (lessonUuid) => {
  return httpClient.post(`/apis/attempt/user-attempt/delete/${lessonUuid}`);
};

export default {
  fetchAggregatedPollAttempt,
  fetchPollAttemptList,
  fetchPollAttemptRaw,
  fetchPollsAttemptRaw,
  fetchUserPollAttempt,
  fetchQuizAttempt,
  fetchQuizAttemptRaw,
  fetchQuizQuestionAttempt,
  fetchQuizQuestionAttemptRaw,
  fetchQuestionAttempt,
  fetchQuestionAttemptRaw,
  fetchQuestionsAttemptRaw,
  fetchPackageCompletionInfo,
  fetchSectionPageProgress,
  fetchCourseSectionProgress,
  fetchPageProgress,
  markAsComplete,
  markSectionAsComplete,
  markSectionAsIncomplete,
  submitPollAttempt,
  createPendingQuestionAttempt,
  restartQuizAttempt,
  startQuizAttempt,
  getQuizAttemptMarkStatus,
  submitQuizAttempt,
  markAsCompleteQuizAttempt,
  submitQuizForStudent,
  massSubmitForStudent,
  checkMassSubmitHasEntry,
  submitQuestionAttempt,
  saveQuestionAttempt,
  checkAssignmentAccess,
  saveViewCount,
  fetchQuestionForAggregatedResponse,
  fetchAggregatedStudentAttempts,
  fetchActivityAccess,
  fetchActivityAccessImmediately,
  fetchSectionQuizActivityInfo,
  updateLastAccessedDate,
  fetchMarkStatus,
  fetchQuestionLFAStatus,
  fetchQuestionMultipleLFAStatus,
  reevaluateQuestionLFAFeedback,
  fetchLessonSettings,
  createPendingQuestionAttemptForPrintView,
  saveQuestionAttemptForPrintView,
  startAttempt,
  retryQuizAttempt,
  saveNumOfHintsUsed,
  startActivity,
  fetchLessonActivities,
  checkIsValidActivity,
  fetchDiscussionAttempt,
  fetchQuizLFAStatus,
  fetchStudentQuizAttempts,
  checkAnswer,
  fetchLFADrafts,
  fetchHasAttempts,
  deletePreviewAttempts,
};
